import { Btn } from "@/components/ui/Btn/Btn";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { PLANS } from "@notamen/core/billing";
import type { Plan } from "@notamen/core/billing";
import styles from "./PricingPage.module.css";

const ORDER: Array<keyof typeof PLANS> = ["solo", "studio", "agency", "enterprise"];

function formatPrice(plan: Plan): string {
  if (plan.priceMonthly === null) return "Custom";
  return `$${plan.priceMonthly}`;
}

export function PricingPage() {
  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.kicker}>Pricing</p>
            <h1 className={styles.title}>
              Pick by how many brands you run.
            </h1>
            <p className={styles.lead}>
              We're onboarding new workspaces in waves. Pricing kicks in only
              when you're invited off the waitlist — no card collected up
              front.
            </p>
          </div>
        </section>

        <section className={styles.plansSection}>
          <div className={styles.plans}>
            {ORDER.map((id) => {
              const plan = PLANS[id];
              return (
                <article
                  key={plan.id}
                  className={`${styles.plan} ${plan.recommended ? styles.recommended : ""}`}
                >
                  {plan.recommended && <div className={styles.badge}>Most popular</div>}

                  <header className={styles.planHeader}>
                    <h2 className={styles.planName}>{plan.name}</h2>
                    <div className={styles.priceRow}>
                      <span className={styles.price}>{formatPrice(plan)}</span>
                      <span className={styles.cadence}>{plan.cadence}</span>
                    </div>
                    <p className={styles.blurb}>{plan.blurb}</p>
                  </header>

                  <div className={styles.ctaWrap}>
                    <Btn
                      href={plan.id === "enterprise" ? "mailto:sales@socialmediamanager.pheonixapps.com" : "/signup"}
                      variant={plan.recommended ? "primary" : "secondary"}
                      size="md"
                    >
                      {plan.id === "enterprise" ? plan.cta : "Join waitlist"}
                    </Btn>
                  </div>

                  <ul className={styles.features}>
                    {plan.features.map((f) => (
                      <li key={f} className={styles.feature}>
                        <span className={styles.check} aria-hidden="true">
                          ✓
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.faqSection}>
          <div className={styles.faqInner}>
            <h2 className={styles.faqTitle}>Common questions</h2>
            <dl className={styles.faqs}>
              <div className={styles.faq}>
                <dt className={styles.faqQ}>How does the waitlist work?</dt>
                <dd className={styles.faqA}>
                  We invite new workspaces in small batches so onboarding stays
                  hands-on. Pick a workspace name when you join, and we'll
                  email you when access opens — that's when you pick a plan
                  and start drafting.
                </dd>
              </div>
              <div className={styles.faq}>
                <dt className={styles.faqQ}>What counts as a draft?</dt>
                <dd className={styles.faqA}>
                  One generation, regardless of how many platforms or
                  variants you ask for at once. Asking for an IG carousel +
                  TikTok script + FB caption from the same input = one
                  draft.
                </dd>
              </div>
              <div className={styles.faq}>
                <dt className={styles.faqQ}>Can I change plans later?</dt>
                <dd className={styles.faqA}>
                  Up or down, any time. Prorated. The new draft cap takes
                  effect immediately; the next invoice reflects the change.
                </dd>
              </div>
              <div className={styles.faq}>
                <dt className={styles.faqQ}>Where does my data live?</dt>
                <dd className={styles.faqA}>
                  AWS us-east-1, in your own row partition of a multi-tenant
                  DynamoDB table. Enterprise customers can request a
                  dedicated AWS sub-account.
                </dd>
              </div>
            </dl>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
